import {
  useState,
  createContext,
  useContext,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSkipioApi, touchSkipioApi } from "../api";
import { useSkipioQuery } from "./use-query";

type LanguageOptions = "en" | "es" | "default";
type Context = [LanguageOptions, Dispatch<SetStateAction<LanguageOptions>>];

const context = createContext<Context | null>(null);

function parseLanguageCode(code = window.navigator.language) {
  const language = code.slice(0, 2);

  switch (language) {
    case "es":
      return "es";
    default:
      return "en";
  }
}

export function Provider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<LanguageOptions>(() => {
    return parseLanguageCode(
      window.Localize
        ? window.Localize.getLanguage()
        : window.navigator.language
    );
  });

  useEffect(() => {
    if (!window.Localize) return;

    function handler(data: { to: LanguageOptions }) {
      const newLanguage = data.to;
      setLanguage(parseLanguageCode(newLanguage));
    }

    window.Localize.on("setLanguage", handler);
    return () => {
      if (!window.Localize) return;
      window.Localize.off("setLanguage", handler);
    };
  }, []);

  useEffect(() => {
    if (window.Localize) {
      window.Localize.setLanguage(language);
    }
  }, [language]);

  return (
    <context.Provider value={[language, setLanguage]}>
      {children}
    </context.Provider>
  );
}

export default function useLanguage(loadUser = true) {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const user =
    useSkipioQuery({
      queryKey: ["userProfile"],
      enabled: loadUser,
    }).data || null;

  const [language, setLanguage] = useContext(context)!;

  useEffect(() => {
    if (user && language !== user.language) {
      setLanguage(user.language);
    }
  }, [user]);

  async function setLanguageAsync(lang: LanguageOptions) {
    if (lang === language) return;

    if (loadUser) {
      await skipioApi.userProfile.put(
        {},
        {
          data: {
            user: {
              language: lang,
            },
          },
        }
      );

      await touchSkipioApi("userProfile");
      await queryClient.invalidateQueries({ queryKey: ["userProfile"] });
    }

    setLanguage(lang);
  }

  return {
    language,
    setLanguage: setLanguageAsync,
    selectFromLanguage(object: Partial<Record<LanguageOptions, ReactNode>>) {
      if (object[language]) return object[language];
      else return object.default;
    },
  };
}
